import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { Container, Grid, Cell } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import RichText from 'components/RichText';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { small } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import TransitionObserver from 'utils/TransitionObserver';

import LearnMore from './LearnMore';

class ShowcaseRowsMobile extends PureComponent {
  constructor(props) {
    super(props);
    this.rowRefs = map(props.rows, () => React.createRef());
    this.transitionObserver = new TransitionObserver(this.rowRefs, props.handleRowTransition);
  }

  componentDidMount() {
    this.transitionObserver.observe();
  }

  componentWillUnmount() {
    this.transitionObserver.unobserve();
  }

  render() {
    const { rows, trackEvent } = this.props;

    return (
      <div className="ShowcaseRowsMobile">
        <Container>
          <Grid isCentered>
            <Cell small={{ columns: 12 }} mediumUp={{ columns: 11 }}>
              {map(rows, (content, _index) => (
                <div
                  className="ShowcaseRowsMobile__row"
                  ref={this.rowRefs[_index]}
                  id={`row-${_index}`}
                  key={`row-${_index}`}
                >
                  <SecondaryHeading>{content.heading}</SecondaryHeading>
                  <div className="ShowcaseRowsMobile__image">
                    <Image
                      className="ShowcaseRowsMobile__content-image"
                      src={get(content, ['mobileImage', 'file', 'cloudinaryUrl'])}
                      alt={get(content, ['mobileImage', 'title'])}
                      width={IMAGE_WIDTHS.W_1000}
                      ratio={IMAGE_RATIOS.R_1x1}
                    />
                  </div>
                  <div className="ShowcaseRowsMobile__content">
                    <RichText content={content.subtext} />

                    <ul className="ShowcaseRowsMobile__learn-more">
                      {map(content.learnMoreLinks, ({ text, url, isDisabled }, index) => {
                        return (
                          <li
                            className="ShowcaseRowsMobile__learn-more-item"
                            key={`learn-more-${index}`}
                          >
                            <LearnMore
                              text={text}
                              href={url}
                              trackEvent={trackEvent}
                              isDisabled={isDisabled}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </Cell>
          </Grid>
        </Container>

        <style jsx>{`
          .ShowcaseRowsMobile {
            position: relative;
            overflow: hidden;
          }

          .ShowcaseRowsMobile__row {
            padding-bottom: ${Theme.get('spacing') * 10}px;
          }

          @media ${small} {
            .ShowcaseRowsMobile__row {
              padding-bottom: ${Theme.get('spacing') * 4}px;
            }
          }

          .ShowcaseRowsMobile__row:last-child {
            padding-bottom: 0;
          }

          .ShowcaseRowsMobile__content {
            height: 100%;
            padding: ${Theme.get('spacing') * 6}px 0;
          }

          .ShowcaseRowsMobile__content-link-container--add-bottom-margin {
            margin-bottom: ${Theme.get('spacing') * 4}px;
          }

          .ShowcaseRowsMobile__learn-more-item {
            border-top: 1px solid ${Theme.get('colors-black-80')};
          }

          .ShowcaseRowsMobile__learn-more-item:last-child {
            border-bottom: 1px solid ${Theme.get('colors-black-80')};
          }
        `}</style>

        <style jsx global>{`
          .ShowcaseRowsMobile .typography--secondary-heading {
            margin-bottom: ${Theme.get('spacing') * 4}px;
          }

          .ShowcaseRowsMobile .AspectRatio {
            background-color: ${Theme.get('colors-grey')};
          }

          .ShowcaseRowsMobile .UnorderedList {
            margin-bottom: ${Theme.get('spacing') * 6}px;
          }

          .ShowcaseRowsMobile .UnorderedList .Paragraph {
            margin: 0;
          }
        `}</style>
      </div>
    );
  }
}

ShowcaseRowsMobile.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      tabText: PropTypes.string.isRequired,
      subtext: PropTypes.shape({}).isRequired,
      ctaLinks: PropTypes.arrayOf(
        PropTypes.shape({
          _contentType: PropTypes.string.isRequired,
          internalName: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          openInNewWindow: PropTypes.bool.isRequired,
        })
      ),
      desktopImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      desktopImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
      mobileImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      mobileImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
    })
  ).isRequired,
  handleRowTransition: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

export default ShowcaseRowsMobile;
